import { Component, h } from 'preact'
import Header from '../header'
import axios from 'axios'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { KeyInsightCard } from './KeyInsightCard'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import { FilterOptionDate, FilterOptionItem, StoredState } from './interfaces'
import {
  getDefaultDateRange,
  normalizeUserRoles,
  trackDashboardUsage, getDefaultSelectedDate, normalizeCategories
} from './utils'
import DashboardSimpleFilter from './DashboardSimpleFilter'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import DashboardCategoryFilter from './DashboardCategoryFilter'
import QueryParameters from './QueryParameters'
import {subscribe} from '../../utils/globals'


interface Props {
  categories: any[]
}


interface State {
  questionsAsked: number,
  questionsAnswered: number,
  percentQuestionsAnswered: number,
  singleUserTopics: number,
  percentAnsweredByPeer: number,
  averageResponseTime: string,
  userRoles: Array<FilterOptionItem>,
  selectedRoles: Array<string>,
  isUserRoleDropdownOpen: boolean,
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isDateRangeOpen: boolean,
  selectedCategories: Array<string>,
  isCategoryDropdownOpen: boolean,
  isLoading: boolean,
  storedState: StoredState,
  isKeyInsightLoading: boolean,
  isSidebarMenuOpen: boolean
}

class SuccessDashboard extends Component<Props, State> {
  wrapperRef: null | HTMLElement = null
  constructor () {
    super()
    let monthStartDate = AnalyticsDateFormatter.getMonthStartDate()
    let yesterday = AnalyticsDateFormatter.getYesterday()
    const queryParams = new QueryParameters()
    this.setState({
      questionsAsked: 0,
      questionsAnswered: 0,
      percentQuestionsAnswered: 0,
      singleUserTopics: 0,
      percentAnsweredByPeer: 0,
      averageResponseTime: '',
      userRoles: [],
      selectedRoles: queryParams.has('roles') ? queryParams.get('roles').split(',') : [],
      isUserRoleDropdownOpen: false,
      selectedPresetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
      storedState: {
        presetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
        selectedDateRange: getDefaultSelectedDate()
      },
      startDate: monthStartDate,
      endDate: yesterday,
      presetDateRangeList: [],
      isDateRangeOpen: false,
      isLoading: false,
      isKeyInsightLoading: false,
      selectedCategories: queryParams.has('categories') ? queryParams.get('categories').split(',') : [],
      isCategoryDropdownOpen: false,
      isSidebarMenuOpen: true
    })
    this.toggleUserRoleDropdown = this.toggleUserRoleDropdown.bind(this)
    this.onUserRolesChanged = this.onUserRolesChanged.bind(this)
    this.onResetUserRoles = this.onResetUserRoles.bind(this)
    this.onClearUserRoles = this.onClearUserRoles.bind(this)

    this.toggleDateRange = this.toggleDateRange.bind(this)
    this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
    this.onResetDateRange = this.onResetDateRange.bind(this)
    this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
    this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)

    this.toggleCategoryDropdown = this.toggleCategoryDropdown.bind(this)
    this.onResetCategories = this.onResetCategories.bind(this)
    this.onCategoriesChanged = this.onCategoriesChanged.bind(this)
    this.onClearCategories = this.onClearCategories.bind(this)

  }

  async componentDidMount () {
    trackDashboardUsage('success')
    subscribe('control_sidebar_state', (payload) => this.setState({ isSidebarMenuOpen: payload.isOpen }))
    this.setState({
      isKeyInsightLoading: true,
      isLoading: true
    })
    try {
      const [presetDateRanges, mainRoles] = await Promise.all([this.fetchDateRangeFilter(), this.fetchUserRoles()])
      let defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
      let updatedState = Object.assign({}, this.state.storedState)
      updatedState.presetDateRangeId = defaultDateRange.id
      updatedState.selectedDateRange = defaultDateRange

      if ('custom_range' === this.state.selectedPresetDateRangeId) {
        const queryParameters = new QueryParameters()
        defaultDateRange = {
          id: 'custom_range',
          label: 'Custom range',
          startDate: new Date(queryParameters.get('date_from')),
          endDate: new Date(queryParameters.get('date_till'))
        }
        updatedState.selectedDateRange = getDefaultSelectedDate()
      }

      this.setState({
        isLoading: false,
        userRoles: mainRoles,
        presetDateRangeList: presetDateRanges,
        selectedDate: defaultDateRange,
        startDate: new Date(defaultDateRange.startDate),
        endDate: new Date(defaultDateRange.endDate),
        storedState: updatedState
      })
    } catch {
      this.setState({ isLoading: false })
    }
      this.fetchSuccessKeyInsights()
  }

  addHandler () {
    const { isDateRangeOpen, isUserRoleDropdownOpen, isCategoryDropdownOpen } = this.state
    if(isDateRangeOpen || isUserRoleDropdownOpen || isCategoryDropdownOpen ) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({
        isUserRoleDropdownOpen: false,
        isCategoryDropdownOpen: false
      }, this.removeHandler)
    }
  }

  async fetchUserRoles () {
    const res = await axios.get('/role/fetchRolesForFilter')
    return normalizeUserRoles(res.data)
  }

  async refetchData () {
    this.fetchSuccessKeyInsights()
  }

  async fetchDateRangeFilter () {
    const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
    return res.data.ranges
  }

  async fetchSuccessKeyInsights () {
    this.setState({isKeyInsightLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/successKeyInsights')
      const {questionsAsked, questionsAnswered, percentQuestionsAnswered, singleUserTopics, percentAnsweredByPeer, averageResponseTime} = res.data
      this.setState({
        questionsAsked: questionsAsked,
        questionsAnswered: questionsAnswered,
        percentQuestionsAnswered: percentQuestionsAnswered,
        singleUserTopics: singleUserTopics,
        percentAnsweredByPeer: percentAnsweredByPeer,
        averageResponseTime: averageResponseTime,
        isKeyInsightLoading: false
      })
    } catch {
      this.setState({isKeyInsightLoading: false})
    }
  }


  async fetchData (url: string) {
    const { startDate, endDate, selectedRoles, selectedCategories } = this.state
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false),
      roles: selectedRoles,
      categories: selectedCategories
    }
    return axios.post(url, {
      filters: filterOptions
    })
  }

  toggleUserRoleDropdown (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }

    this.setState({isUserRoleDropdownOpen: value}, this.addHandler)
  }

  onUserRolesChanged (data: Array<string>) {
    this.setState({
      isUserRoleDropdownOpen: false,
      selectedRoles: data
    })
    window.history.pushState(null, 'roles changed', (new QueryParameters()).set('roles', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetUserRoles () {
    this.setState({
      isUserRoleDropdownOpen: false
    })
  }

  onClearUserRoles (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedRoles: [],
      isUserRoleDropdownOpen: false
    })
    window.history.pushState(null, 'roles cleared', (new QueryParameters()).unset('roles').getQueryString())
    this.refetchData()
  }

  toggleDateRange (value: boolean) {
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }
    if (this.state.isCategoryDropdownOpen) {
      this.onResetCategories()
    }
    this.setState({ isDateRangeOpen: value }, this.addHandler)
  }

  onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
    this.setState({
      selectedPresetDateRangeId: selectedDate.id,
      selectedDate: selectedDate
    })
  }

  onCustomDateRangeChanged (dates: Date[]) {
    const customSelectedDate = {
      id: 'custom_range',
      label: 'Custom Range',
      startDate: dates[0],
      endDate: dates[1]
    }
    this.setState({
      selectedDate: customSelectedDate,
      selectedPresetDateRangeId: customSelectedDate.id
    })
  }

  onDateRangeChanged () {
    const { selectedDate, selectedPresetDateRangeId } = this.state
    let updatedState = Object.assign({}, this.state.storedState)
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = selectedDate
    this.setState({
      isDateRangeOpen: false,
      startDate: new Date(selectedDate.startDate),
      endDate: new Date(selectedDate.endDate),
      storedState: updatedState
    })
    const queryString = new QueryParameters()
    queryString.set('date_range', selectedPresetDateRangeId)
    if (selectedPresetDateRangeId != 'custom_range') {
      queryString.unset('date_from')
        .unset('date_till')
    } else {
      queryString.set('date_from', AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate))
        .set('date_till', AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate))
    }
    window.history.pushState(null, 'date range changed', queryString.getQueryString())
    this.refetchData()
  }

  onResetDateRange () {
    const { presetDateRangeId, selectedDateRange } = this.state.storedState
    this.setState({
      isDateRangeOpen: false,
      selectedPresetDateRangeId: presetDateRangeId,
      selectedDate: selectedDateRange
    })
  }

  toggleCategoryDropdown  (value: boolean) {
    if (this.state.isDateRangeOpen) {
      this.onResetDateRange()
    }
    if (this.state.isUserRoleDropdownOpen) {
      this.onResetUserRoles()
    }

    this.setState({ isCategoryDropdownOpen: value }, this.addHandler)
  }

  onCategoriesChanged (data: Array<string>) {
    this.setState({
      selectedCategories: data,
      isCategoryDropdownOpen: false
    })
    window.history.pushState(null, 'categories changed', (new QueryParameters()).set('categories', data.join(',')).getQueryString())
    this.refetchData()
  }

  onResetCategories () {
    this.setState({
      isCategoryDropdownOpen: false
    })
  }

  onClearCategories (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({
      selectedCategories: [],
      isCategoryDropdownOpen: false
    })
    window.history.pushState(null, 'categories cleared', (new QueryParameters()).unset('categories').getQueryString())
    this.refetchData()
  }

  render ({ categories }: Props, { isKeyInsightLoading, questionsAsked, questionsAnswered, percentQuestionsAnswered, singleUserTopics, percentAnsweredByPeer, averageResponseTime, presetDateRangeList, userRoles, selectedRoles, isUserRoleDropdownOpen,  isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, isLoading, selectedDate, selectedCategories, isCategoryDropdownOpen, isSidebarMenuOpen}: State) {
    return (
      <div className={`success-dashboard-overview dashboard-overview  ${isSidebarMenuOpen ? 'dashboard-overview-open-menu' : 'dashboard-overview-closed-menu'}`} style={{ width: `calc(100vw - ${isSidebarMenuOpen ? '376' : '152'}px)` }}>
        <Header
          title='Success Dashboard'
          disableCancelButton
          disableSaveButton
        />
       <div className='filter-container' ref={node => this.wrapperRef = node}>
          <DashboardDateRangeFilter isLoading={isLoading}
                                    startDate={startDate}
                                    endDate={endDate}
                                    isDropdownOpen={isDateRangeOpen}
                                    items={presetDateRangeList}
                                    selectedDate={selectedDate}
                                    selectedPresetId={selectedPresetDateRangeId}
                                    toggleDropdown={this.toggleDateRange}
                                    onChange={this.onDateRangeChanged}
                                    onReset={this.onResetDateRange}
                                    onCustomDateChange={this.onCustomDateRangeChanged}
                                    onPresetDateChange={this.onPresetDateRangeChanged} />
          <DashboardCategoryFilter isLoading={isLoading}
                                 id='categories'
                                 name='(parent) category or group'
                                 customClassName='categories'
                                 title='Published in'
                                 isDropdownOpen={isCategoryDropdownOpen}
                                 toggleDropdown={this.toggleCategoryDropdown}
                                 resetDropdown={this.onResetCategories}
                                 onClear={this.onClearCategories}
                                 onChange={this.onCategoriesChanged}
                                 items={normalizeCategories(categories)}
                                 selectedItems={selectedCategories} />
          <DashboardSimpleFilter isLoading={isLoading}
                                 id='userRole'
                                 name='user role'
                                 customClassName='user-role'
                                 title='User role'
                                 isDropdownOpen={isUserRoleDropdownOpen}
                                 toggleDropdown={this.toggleUserRoleDropdown}
                                 resetDropdown={this.onResetUserRoles}
                                 onClear={this.onClearUserRoles}
                                 onChange={this.onUserRolesChanged}
                                 items={userRoles}
                                 selectedItems={selectedRoles} />
        </div>
        <div className='metric-container'>
          <KeyInsightCard title='Questions asked'
            total={questionsAsked}
            titleTooltip='The number of questions asked in the selected time period.'
            hideComparisionWithPreviousPeriod
            isLoading={isKeyInsightLoading} />
          <KeyInsightCard title='Questions answered'
            total={questionsAnswered}
            titleTooltip='The number of questions asked in the selected time period that also have an answer marked.'
            hideComparisionWithPreviousPeriod
            isLoading={isKeyInsightLoading} />
          <KeyInsightCard title='% Questions answered'
            total={percentQuestionsAnswered}
            titleTooltip='The % of questions created in the selected time period which have an answer marked.'
            hideComparisionWithPreviousPeriod
            isPercentage
            isLoading={isKeyInsightLoading} />
          <KeyInsightCard title='Single user topics'
            total={singleUserTopics}
            titleTooltip='The number of topics in the selected time period where only one user contributed content.'
            hideComparisionWithPreviousPeriod
            isLoading={isKeyInsightLoading} />
          <KeyInsightCard title='% Answered by peer'
            total={percentAnsweredByPeer}
            titleTooltip='The % of answers where the author of the best answer has the registered user or superuser role. Only includes answers that were marked in the selected time period.'
            hideComparisionWithPreviousPeriod
            isPercentage
            isLoading={isKeyInsightLoading} />
          <KeyInsightCard title='Average response time'
            total={averageResponseTime}
            titleTooltip="The average amount of time between the creation of a topic and its first reply. Replies from topic starter don't count."
            hideComparisionWithPreviousPeriod
            isLoading={isKeyInsightLoading} />
        </div>
        <DashboardTimezoneDisclaimer />
      </div>)
  }
}

export default SuccessDashboard
